import { Location } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, NgZone, Provider, inject } from '@angular/core';
import { NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { catchError, defer, filter, map, merge, of, switchMap, tap, timer } from 'rxjs';

import { ConfigurationService } from '../configuration';
import { JAP_PROPERTIES_ENDPOINT } from '../endpoint.consts';
import { Logger } from '../logging/logger.service';
import { WINDOW_TOKEN } from '../window.consts';

const ONE_HOUR = 1000 * 60 * 60;
const REFRESH_INTERVAL = new InjectionToken<number>('refresh-interval', { factory: () => ONE_HOUR });

/**
 *  Use to refresh your app on new version release by calling
 *  {@link setupReloadAppEveryNightOrIfStaleAppOnUserNavigation} to set up automatic reloading behavior.
 *
 *  Can be customized with {@link configureRefreshService}
 *  @example
 *  @NgModule({
 *      providers: [
 *          configureRefreshService({
 *               refreshInterval: 60_000 // new version will be checked every minute
 *          })
 *      ]
 *  })
 *  export class AppModule {}
 */
@Injectable({
    providedIn: 'root'
})
export class RefreshService {
    #initialVersion = inject(ConfigurationService).getSync('version');
    #httpClient = new HttpClient(inject(HttpBackend)); // avoid all http interceptors
    #location = inject(Location);
    #logger = inject(Logger).createCustomLogger('RefreshService');
    #ngZone = inject(NgZone);
    #refreshInterval = inject(REFRESH_INTERVAL);
    #router = inject(Router);
    #window = inject(WINDOW_TOKEN);
    #started = false;
    #navigationStarts$ = this.#router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart));
    #navigationError = this.#router.events.pipe(filter((e): e is NavigationError => e instanceof NavigationError));
    #currentVersion = this.#initialVersion;
    #onceEveryDayAt5AMApproximatelyTrigger$ = defer(() => timer(this.#next5AmApproximately()));
    #version$ = this.#httpClient.get<{ version: string }>(JAP_PROPERTIES_ENDPOINT).pipe(
        map(res => res?.version),
        catchError(() => {
            this.#logger.error(`Could not fetch new version from ${JAP_PROPERTIES_ENDPOINT}`);
            // return current version to avoid a page reload
            return of(this.#currentVersion);
        }),
        filter(Boolean)
    );
    #updateVersion$ = timer(this.#refreshInterval, this.#refreshInterval).pipe(switchMap(() => this.#version$));

    #reloadNeeded$ = merge(
        // 1. reload every night
        this.#onceEveryDayAt5AMApproximatelyTrigger$.pipe(tap(() => this.#logger.warn(`Nightly reloading.`))),
        // 2. reload when the version changes (schedule polling) on the navigation events only
        this.#navigationStarts$.pipe(
            filter(() => this.#currentVersion !== this.#initialVersion),
            tap(e => this.#logFoundNewVersion(e.url))
        ),
        // 3. reload on 'Failed to fetch dynamically imported module' error on the navigation events only. This can append in 2 cases:
        // a) There is newer version on the server,
        // b) the server was not accessible. In that case, Angular will *not* retry to get the missing chunk.
        this.#navigationError.pipe(
            filter(() => this.#window.navigator.onLine),
            filter(e => e.error.message?.startsWith('Failed to fetch dynamically imported module:')),
            switchMap(e =>
                this.#version$.pipe(
                    tap(v => (this.#currentVersion = v)),
                    map(() => e)
                )
            ),
            tap(e => this.#logFoundNewVersion(e.url))
        )
    );

    /* returns a date at around 5 am. Minute and second are randomized to spread reloads and relieve backends */
    #next5AmApproximately(): Date {
        return new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getHours() >= 5 ? new Date().getDate() + 1 : new Date().getDate(),
            5,
            this.#getRandomInt(5),
            this.#getRandomInt(59)
        );
    }

    #getRandomInt(max: number): number {
        // eslint-disable-next-line sonarjs/pseudo-random
        return Math.floor(Math.random() * max);
    }

    #logFoundNewVersion(url: string): void {
        if (this.#currentVersion !== this.#initialVersion) {
            this.#logger.warn(
                `A newer version (${this.#currentVersion}, was ${this.#initialVersion}) has been deployed, start reloading on page ${url}`
            );
        } else {
            this.#logger.warn(
                `No newer version found on the server (${this.#currentVersion}) but the chunk is missing, start reloading on page ${url}`
            );
        }
    }

    /**
     * @deprecated due to method name does not match its intention after functional extension, use setupReloadAppEveryNightOrIfStaleAppOnUserNavigation() instead.
     * See the README file for additional information.
     */
    setupReloadStaleAppOnUserNavigationOrAfterMidnight(): void {
        this.setupReloadAppEveryNightOrIfStaleAppOnUserNavigation();
    }

    setupReloadAppEveryNightOrIfStaleAppOnUserNavigation(): void {
        if (this.#started) {
            throw new Error('setupReloadAppEveryNightOrIfStaleAppOnUserNavigation() must be only called once.');
        }
        this.#started = true;
        this.#ngZone.runOutsideAngular(() => {
            this.#updateVersion$.subscribe(v => (this.#currentVersion = v));
            this.#reloadNeeded$.subscribe(event => {
                if (event instanceof RouterEvent && event.url !== undefined) {
                    // this will update the URL of the browser without reloading the page
                    this.#location.go(event.url);
                }
                this.#window.location.reload();
            });
        });
    }
}

export function configureRefreshService(options: RefreshServiceOptions): Provider[] {
    return [{ provide: REFRESH_INTERVAL, useValue: options.refreshInterval }];
}

export interface RefreshServiceOptions {
    refreshInterval: number;
}
